import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import MDEditor from '@uiw/react-md-editor';
import axios from 'axios';

import './headerBar.css';
import {
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCloseButton,
    CCol,
    CContainer,
    CHeader,
    CHeaderBrand,
    CHeaderDivider,
    CHeaderNav,
    CImage,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CNavbar,
    CNavbarBrand,
    CNavbarNav,
    CNavItem,
    CNavLink,
    COffcanvas,
    COffcanvasBody,
    COffcanvasHeader,
    COffcanvasTitle,
    CRow,
    CFormSwitch,
    CDropdown,
    CDropdownToggle,
    CDropdownMenu,
    CDropdownItem,
    CDropdownDivider,
    CFormText,
    CFormInput
} from "@coreui/react-pro";
import { setNotificationHeader, setNotificationMessage, setNotificationType } from "../config/Redux/actions";
import { connect } from "react-redux";
import {cilBell, cilReload, cilX, cilChatBubble, cilPhone, cilApps, cilLockLocked} from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { push_notifications, wallboard_api } from "../config/config";
import notification from "../notification/Notification";
import {useMsal} from "@azure/msal-react";

// Custom hook for address validation
const useAddressValidation = () => {
    const [address, setAddress] = useState('');
    const [validationState, setValidationState] = useState({
        isValid: null,
        isValidating: false,
        error: null,
        suggestions: []
    });

    const debounceRef = useRef(null);

    // Basic address pattern validation
    const validateAddressPattern = (addr) => {
        // Basic pattern: number + street name + city + state + zip
        const addressPattern = /^\d+\s+[a-zA-Z0-9\s,.-]+(?:\s+(?:apt|apartment|unit|ste|suite|#)\s*[a-zA-Z0-9-]+)?\s*,?\s*[a-zA-Z\s]+,?\s*[A-Za-z]{2}\s+\d{5}(-\d{4})?$/i;
        return addressPattern.test(addr.trim());
    };

    const validateAddress = useCallback(async (addressValue) => {
        if (!addressValue || addressValue.trim().length < 15) {
            setValidationState({
                isValid: null,
                isValidating: false,
                error: null,
                suggestions: []
            });
            return;
        }

        setValidationState(prev => ({
            ...prev,
            isValidating: true,
            error: null
        }));

        try {
            // First check basic pattern
            const patternValid = validateAddressPattern(addressValue);

            if (!patternValid) {
                setValidationState({
                    isValid: false,
                    isValidating: false,
                    error: 'Please enter address in format: 123 Main Street City ST 12345',
                    suggestions: []
                });

            }else{
                setValidationState({
                    isValid: true,
                    isValidating: false,
                    suggestions: []
                })

            }

        } catch (error) {
            setValidationState({
                isValid: false,
                isValidating: false,
                error: 'Unable to validate address. Please ensure it\'s properly formatted.',
                suggestions: []
            });
        }
    }, []);

    const handleAddressChange = useCallback((value) => {
        setAddress(value);

        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Reset validation state when user starts typing
        if (validationState.isValid !== null) {
            setValidationState(prev => ({
                ...prev,
                isValid: null,
                error: null,
                suggestions: []
            }));
        }

        // Debounce validation by 750ms
        debounceRef.current = setTimeout(() => {
            validateAddress(value);
        }, 750);
    }, [validateAddress, validationState.isValid]);

    const selectSuggestion = useCallback((suggestion) => {
        setAddress(suggestion.formatted);
        setValidationState({
            isValid: true,
            isValidating: false,
            error: null,
            suggestions: []
        });
    }, []);

    const resetValidation = useCallback(() => {
        setAddress('');
        setValidationState({
            isValid: null,
            isValidating: false,
            error: null,
            suggestions: []
        });
    }, []);

    return {
        address,
        setAddress: handleAddressChange,
        validationState,
        selectSuggestion,
        validateAddress: () => validateAddress(address),
        resetValidation
    };
};

function HeaderBar({
                       agent,
                       lob,
                       onLOBChange,
                       showLobModal,
                       setNotificationMessage,
                       setNotificationType,
                       setNotificationHeader
                   }) {
    const [messages, setMessages] = useState({ today: [], yesterday: [], older: [] });
    const [visible, setVisible] = useState(false);
    const [messageVisble, setMessageVisible] = useState(false);
    const [addressModalVisible, setAddressModalVisible] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [activeMessage, setActiveMessage] = useState({});
    const [savedAddress, setSavedAddress] = useState('');
    const websocket = useRef(null);
    const [switchState, setSwitchState] = useState(null);
    const token = localStorage.getItem('authToken');

    // Address validation hook
    const {
        address,
        setAddress,
        validationState,
        selectSuggestion,
        validateAddress,
        resetValidation
    } = useAddressValidation();

    const {instance} = useMsal();

    const handleLogout = (logoutType) => {
        if (logoutType === "popup") {
            instance.logoutPopup({
                postLogoutRedirectUri: "/wallboard",
                mainWindowRedirectUri: "/wallboard"
            });
        } else if (logoutType === "redirect") {
            instance.logoutRedirect({
                postLogoutRedirectUri: "/wallboard"
            });
        }
    }

    useEffect(() => {
        if (agent && agent.id) {
            console.log("Fetching routing profile for user id:", agent.id);
            setRoutingProfile(agent.id);

            // Load saved address
            setHomeAddress(agent.id)

        } else {
            console.log("agent.id is null or undefined.", agent);
        }
    }, [agent?.id]);

    const setRoutingProfile = (agentId) => {
        axios.get(`${wallboard_api}/routingprofile?userid=${agentId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        })
            .then(response => {
                console.log("API Response Data:", response.data);
                if (response.data.statusCode === 200) {
                    if (response.data.routingType === 'CHAT') {
                        setSwitchState(true);
                        console.log("Switch state updated to true");
                    } else if (response.data.routingType === 'VOICE') {
                        setSwitchState(false);
                        console.log("Switch state updated to false");
                    } else {
                        console.error("Unexpected routing type:", response.data.routingType);
                    }
                } else {
                    console.error("Unexpected status code:", response.data.statusCode);
                }
            })
            .catch(error => {
                console.error("Error fetching routing profile:", error);
            });
    }

    const setHomeAddress = (agentId) => {
        axios.get(`${wallboard_api}/address?userId=${agentId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        })
            .then(response => {
                console.log("API Response Data:", response.data);

                if (response.status === 200) {
                    // The Lambda returns a JSON string in the body, so we need to parse it
                    const responseBody = JSON.parse(response.data.body);

                    if (responseBody.homeAddress) {
                        console.log("Home address found:", responseBody.homeAddress);
                        console.log("User ID:", responseBody.userId);

                        setSavedAddress(responseBody.homeAddress);
                    } else {
                        console.log("No home address found for user:", agentId);
                    }
                } else {
                    console.error("Unexpected HTTP status:", response.status);
                }
            })
            .catch(error => {
                console.error("Error fetching home address:", error);

                if (error.response) {
                    // Server responded with error status
                    const errorBody = JSON.parse(error.response.data.body);
                    console.error("Error details:", errorBody.error);

                    if (error.response.status === 404) {
                        console.log("User not found or no home address set for agent:", agentId);
                    } else if (error.response.status === 400) {
                        console.error("Bad request - missing userId parameter");
                    } else if (error.response.status === 403) {
                        console.error("Access denied to Amazon Connect resources");
                    }
                } else {
                    console.error("Network or other error:", error.message);
                }
            });
    };

    const handleSwitchChange = (e) => {
        const newState = e.target.checked;

        const channel = newState ? "chat" : "voice";
        const payload = {
            userId: `${agent.id}`,
            channel: channel
        };

        axios.post(`${wallboard_api}/routingprofile`, payload, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        })
            .then(response => {
                console.log(response);
                if (response.data.statusCode === 200) {
                    showNotification(`<div>Successfully switched your profile to ${channel}</div>`, `<div>Success</div>`, "Success");
                    setSwitchState(newState);
                } else {
                    showNotification(`<div>Failed to update your profile. Contact your supervisor</div>`, `<div>Error</div>`, "Error");
                    setSwitchState(!newState);
                }
                console.log('Channel updated response', response);
            })
            .catch(error => {
                console.error('Error updating channel:', error);
            });
    };

    const handleSaveAddress = async () => {
        if (validationState.isValid !== true) {
            validateAddress();
            return;
        }

        try {
            await axios.post(`${wallboard_api}/address`, {
                userId: agent.id,
                homeAddress: address
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            })
                .then(response => {
                    console.log("API Response Data:", response.data);
                    if (response.data.statusCode === 200) {
                        showNotification(
                            `<div>Home address updated successfully</div>`,
                            `<div>Address Saved</div>`,
                            "Success"
                        );

                        setSavedAddress(address);

                        setAddressModalVisible(false);
                        resetValidation();
                    }else{
                        showNotification(`<div>Failed to update your home address. Contact your supervisor</div>`, `<div>Error</div>`, "Error");
                    }
                })

        } catch (error) {
            console.error('Error saving address:', error);
            showNotification(
                `<div>Failed to save address. Please try again.</div>`,
                `<div>Error</div>`,
                "Error"
            );
        }
    };

    const handleModalClose = () => {
        setAddressModalVisible(false);
        resetValidation();
    };

    const handleModalOpen = () => {
        setAddressModalVisible(true);
        if (savedAddress) {
            setAddress(savedAddress);
        }
    };

    const viewNotification = (message) =>{
        console.log(message)
        setActiveMessage(message);
        setMessageVisible(true);
        message.read = true;

        if (websocket.current) {
            websocket.current.send(JSON.stringify({
                action: 'read',
                id: `${message.id}#${agent.username}`
            }));
        }
        setUnreadCount(prev => prev - 1);
    }

    const deleteNotification = (messageId, category, read) => {
        if(!read){
            console.log("READ STATUS", read)
            setUnreadCount(prev => prev - 1);
        }
        setMessages(prevMessages => {
            const newMessages = {...prevMessages};
            newMessages[category] = newMessages[category].filter(msg => msg.id !== messageId);
            return newMessages;
        });

        if (websocket.current) {
            websocket.current.send(JSON.stringify({
                action: 'delete',
                id: `${messageId}#${agent.username}`
            }));
        }
    }

    function showNotification(message, notificationHeader, notificationType) {
        setNotificationMessage(message);
        setNotificationType(notificationType);
        setNotificationHeader(notificationHeader);
    }

    const formatToLocalDateTime = (isoDateString) => {
        if (!isoDateString) {
            return "N/A";
        }
        const date = new Date(isoDateString.includes('Z') || isoDateString.includes('+') ? isoDateString : isoDateString + 'Z');
        const options = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZoneName: 'short'
        };
        return date.toLocaleString(undefined, options);
    };

    const categorizeDate = (date) => {
        const messageDate = new Date(date);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const messageDateLocal = new Date(messageDate.getTime() - messageDate.getTimezoneOffset() * 60000);
        const todayLocal = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
        const yesterdayLocal = new Date(yesterday.getTime() - yesterday.getTimezoneOffset() * 60000);

        if (messageDateLocal.toDateString() === todayLocal.toDateString()) {
            return 'today';
        } else if (messageDateLocal.toDateString() === yesterdayLocal.toDateString()) {
            return 'yesterday';
        } else {
            return 'older';
        }
    };

    useEffect(() => {
        if(agent.id === ''){
            return;
        }

        function connect() {
            websocket.current = new WebSocket(`${push_notifications}?token=Bearer ${token}&userId=${agent.id}`);

            websocket.current.onopen = () => {
                console.log('WebSocket Connected');
            };

            websocket.current.onclose = event => {
                console.log('Notification WebSocket Disconnected. Attempting to reconnect...', event.reason);
                setTimeout(() => {
                    setMessages({ today: [], yesterday: [], older: [] });
                    setUnreadCount(0);
                    connect();
                }, 1000);
            };

            websocket.current.onmessage = event => {
                console.log('[WebSocket] Received raw message:', event.data);

                const newMessage = JSON.parse(event.data);

                if (newMessage.expiry) {
                    console.log('[WebSocket] Message expiry timestamp:', newMessage.expiry);
                    console.log('[WebSocket] Current timestamp:', new Date().toISOString());
                } else {
                    console.log('[WebSocket] No expiry found in message.');
                }

                const category = categorizeDate(newMessage.date);
                console.log("NEW MESSAGE CAT", newMessage.date, category);

                setMessages(prev => {
                    const sortedMessages = [...prev[category], newMessage].sort((a, b) => new Date(b.date) - new Date(a.date));
                    return {
                        ...prev,
                        [category]: sortedMessages
                    };
                });

                if (!newMessage.read) {
                    setUnreadCount(prev => prev + 1);
                    if (!newMessage.past) {
                        showNotification(`<div>${newMessage.message}</div>`, `<div>${newMessage.subject}</div>`, "Success");
                    }
                }
            };

            websocket.current.onerror = error => {
                console.log('WebSocket Error:', error);
                setMessages({ today: [], yesterday: [], older: [] })
                setUnreadCount(0)
            };
        }

        connect();

        const checkExpiry = () => {
            const now = new Date();
            console.log('[ExpiryCheck] Checking message expiry at:', now.toISOString());

            setMessages(prevMessages => {
                let unreadAdjustment = 0;

                const filterExpired = (msgs) =>
                    msgs.filter(msg => {
                        const expired = msg.expiry && new Date(msg.expiry) <= now;
                        if (expired && !msg.read) {
                            // Decrease unread count for expired unread messages
                            unreadAdjustment++;
                            console.log('[ExpiryCheck] Message expired and removed:', msg);
                        }
                        return !msg.expiry || new Date(msg.expiry) > now;
                    });

                const updatedMessages = {
                    today: filterExpired(prevMessages.today),
                    yesterday: filterExpired(prevMessages.yesterday),
                    older: filterExpired(prevMessages.older),
                };

                // Adjust unread count
                setUnreadCount(prevUnreadCount => Math.max(0, prevUnreadCount - unreadAdjustment));

                return updatedMessages;
            });
        };

        // Calculate delay until top of the next minute
        const now = new Date();
        const delay = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();


        const timeout = setTimeout(() => {
            checkExpiry(); // Run immediately at the top of the next minute
            const interval = setInterval(checkExpiry, 60000); // Run every minute


            return () => clearInterval(interval);
        }, delay);


        return () => {
            if (websocket.current) {
                websocket.current.close();
            }
            clearTimeout(timeout);
        };
    }, [agent.id]);

    return (
        <>
            {/* Message Modal */}
            <CModal visible={messageVisble} onClose={() => setMessageVisible(false)} aria-labelledby="Notification" size="xl">
                <CModalHeader onClose={() => setMessageVisible(false)}>
                    <CModalTitle id="LiveDemoExampleLabel">{activeMessage.subject}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <MDEditor.Markdown source={activeMessage.message} />
                </CModalBody>
                <CModalFooter>
                    <small><b>From: </b>{activeMessage.publisher}</small>
                </CModalFooter>
            </CModal>

            {/* Enhanced Address Modal */}
            <CModal visible={addressModalVisible} onClose={handleModalClose} aria-labelledby="Address" size="lg">
                <CModalHeader onClose={handleModalClose}>
                    <CModalTitle id="AddressModalLabel">Change Home Address</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <div className="position-relative">
                        <CFormInput
                            placeholder="Enter address in the format 123 Main Street, Anytown, CA 90210"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            invalid={validationState.isValid === false}
                            valid={validationState.isValid === true}
                            disabled={validationState.isValidating}
                        />

                        {/* Loading indicator */}
                        {validationState.isValidating && (
                            <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Validating...</span>
                                </div>
                            </div>
                        )}

                        {/* Validation feedback */}
                        {validationState.error && (
                            <div className="invalid-feedback d-block">
                                <CIcon icon={cilX} className="me-1" />
                                {validationState.error}
                            </div>
                        )}

                        {/*{validationState.isValid === true && (*/}
                        {/*    <div className="valid-feedback d-block text-success">*/}
                        {/*        ✓ Address validated successfully*/}
                        {/*    </div>*/}
                        {/*)}*/}

                        {/* Address suggestions */}
                        {validationState.suggestions.length > 0 && (
                            <div className="mt-2">
                                <small className="text-muted fw-bold">Suggested addresses:</small>
                                {validationState.suggestions.map((suggestion, index) => (
                                    <div
                                        key={index}
                                        className="border rounded p-2 mt-1 bg-light hover-bg-primary-subtle"
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'background-color 0.15s ease-in-out'
                                        }}
                                        onClick={() => selectSuggestion(suggestion)}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e7f3ff'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                    >
                                        <small className="d-block">{suggestion.formatted}</small>
                                        <small className="text-muted">Click to select</small>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-3 p-2 bg-light rounded">
                        <small className="text-muted d-block">
                            <strong>Important:</strong> This address will be shared with 911 emergency services when working from home.
                        </small>
                        {savedAddress && (
                            <small className="text-success d-block mt-1">
                                <strong>Current address:</strong> {savedAddress}
                            </small>
                        )}
                    </div>
                </CModalBody>
                <CModalFooter>
                    <div>
                        <CButton onClick={handleModalClose} color="secondary" className="me-2">
                            Cancel
                        </CButton>
                        <CButton
                            onClick={handleSaveAddress}
                            color="success"
                            disabled={validationState.isValid !== true || validationState.isValidating}
                        >
                            {validationState.isValidating ? (
                                <>
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    Validating...
                                </>
                            ) : (
                                'Save Address'
                            )}
                        </CButton>
                    </div>
                </CModalFooter>
            </CModal>

            {/* Notifications Offcanvas */}
            <COffcanvas backdrop={false} placement="right" visible={visible} onHide={() => setVisible(false)}>
                <COffcanvasHeader id="offCanvasHeader">
                    <CCloseButton placement="right" className="text-reset" onClick={() => setVisible(false)} />
                </COffcanvasHeader>
                <COffcanvasBody id="offCanvasBody">
                    {['today', 'yesterday', 'older'].map((category) => (
                        messages[category].length > 0 && (
                            <CCard key={category} id="notifiactionDateCard">
                                <CCardHeader id="notificationDateCardHeader">
                                    <strong>{category.charAt(0).toUpperCase() + category.slice(1)}</strong>
                                </CCardHeader>
                                <CCardBody id="notificationDateCardBody">
                                    {messages[category].map((message, index) => (
                                        <div key={index} className={`border-start border-start-4 py-1 px-3 ${message.read ? "border-start-secondary" : "border-start-success"} border-bottom border-bottom-1 messageContainer`} onClick={()=>viewNotification(message)}>
                                            <div>
                                                <strong>{message.subject}</strong>
                                            </div>
                                            <div className="notificationFooter">
                                                <span>{formatToLocalDateTime(message.date)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </CCardBody>
                            </CCard>
                        )
                    ))}
                </COffcanvasBody>
            </COffcanvas>

            {/* Header */}
            <CHeader position="sticky" id="headerBar">
                <CContainer fluid className="d-flex justify-content-between align-items-center">
                    <CHeaderBrand>
                        <div className="scheduleIconBox">
                            <span color="white">WallBoard</span>
                        </div>
                    </CHeaderBrand>
                    <div>{lob.replace("CCC", "Customer Care Center").toUpperCase()}</div>

                    <CNavbarNav id="notification" className="d-flex flex-row align-items-center gap-2">
                        <CNavbarNav className="border-end pe-3 me-3">
                            <CNavItem className="">
                                <CButton color="dark" onClick={showLobModal}>Change LOB</CButton>
                            </CNavItem>
                        </CNavbarNav>

                        {switchState !== null && (
                            <>
                                <CNavItem className="d-flex align-items-center">
                                    <CIcon icon={cilPhone} size="xl" style={{ transform: 'scaleX(-1)' }} />
                                </CNavItem>

                                <CNavItem className="d-flex align-items-center">
                                    <CFormSwitch
                                        id="customSwitch"
                                        checked={switchState}
                                        onChange={handleSwitchChange}
                                        className="custom-switch form-switch-lg mx-2"
                                    />
                                </CNavItem>

                                <CNavItem className="d-flex align-items-center me-2">
                                    <CIcon icon={cilChatBubble} size="xl" />
                                </CNavItem>
                            </>
                        )}


                        {agent.id &&
                            <CNavbarNav id="notification" className="pe-3">
                                <CNavItem
                                    href="#"
                                    onClick={() => setVisible(true)}
                                    className="position-relative"
                                    disabled={(messages.today.length + messages.yesterday.length + messages.older.length) === 0}
                                >
                                    <CIcon icon={cilBell} size="lg"/>
                                    {unreadCount > 0 &&
                                        <CBadge color="danger" position="top-end" shape="rounded-circle">
                                            {unreadCount} <span className="visually-hidden">unread messages</span>
                                        </CBadge>
                                    }
                                </CNavItem>
                            </CNavbarNav>
                        }

                        <CNavbarNav className="">
                            <CDropdown variant="nav-item" popper={true}>
                                <CDropdownToggle caret={false}>
                                    <CIcon icon={cilApps} size="lg"/>
                                    {(!savedAddress && agent.id) &&
                                        <CBadge color="danger" position="top-end" shape="rounded-circle">
                                            ! <span className="visually-hidden">No home address</span>
                                        </CBadge>
                                    }
                                </CDropdownToggle>
                                <CDropdownMenu>
                                    {agent.id &&
                                        <>
                                            <CDropdownItem href="#" onClick={handleModalOpen}>
                                                Home Address
                                                {savedAddress ? (
                                                    <small className="d-block text-muted text-truncate" style={{maxWidth: '200px'}}>
                                                        {savedAddress}
                                                    </small>
                                                ) : (
                                                    <small className="d-block text-danger" style={{maxWidth: '200px'}}>
                                                        Missing Address
                                                    </small>
                                                )}
                                            </CDropdownItem>
                                            <CDropdownDivider />
                                        </>
                                    }
                                    <CDropdownItem href="#" onClick={() => handleLogout("popup")}>
                                        <CIcon icon={cilLockLocked} className="me-2" />Log Out
                                    </CDropdownItem>
                                </CDropdownMenu>
                            </CDropdown>
                        </CNavbarNav>
                    </CNavbarNav>
                </CContainer>
            </CHeader>
        </>
    );
}

const mapDispatchToProps = {
    setNotificationMessage,
    setNotificationHeader,
    setNotificationType
};

export default connect(null, mapDispatchToProps)(HeaderBar);